<script setup lang="ts">
import { ref, computed } from "vue";
import EmiPriceToggle from "~/components/molecules/EmiPriceToggle.vue";
import {
  isEmiEligible as checkEmiEligible,
  calculateMonthlyInstallment,
  formatPriceNumber,
  DEFAULT_EMI_THRESHOLD,
  DEFAULT_EMI_TENURES,
  DEFAULT_EMI_TENURE,
} from "~/utils/emiCalculator";

const props = withDefaults(
  defineProps<{
    price?: number | null;
    emiThreshold?: number;
    defaultTenure?: number;
    availableTenures?: number[];
  }>(),
  {
    price: 0,
    emiThreshold: DEFAULT_EMI_THRESHOLD,
    defaultTenure: DEFAULT_EMI_TENURE,
    availableTenures: () => [...DEFAULT_EMI_TENURES],
  }
);

const mode = ref<"full" | "monthly">("full");
const selectedTenure = ref<number>(props.defaultTenure);

const isEmiEligible = computed(() => {
  return checkEmiEligible(props.price, props.emiThreshold);
});

const formattedFullPrice = computed(() => {
  return formatPriceNumber(props.price);
});

const monthlyInstallment = computed(() => {
  return calculateMonthlyInstallment(props.price, selectedTenure.value);
});

const formattedMonthlyPrice = computed(() => {
  return formatPriceNumber(monthlyInstallment.value);
});
</script>

<template>
  <div v-if="price" class="flex flex-col gap-3 font-medium w-full">
    <!-- Common EMI & Mode Toggle Component -->
    <EmiPriceToggle
      v-if="isEmiEligible"
      v-model="mode"
      v-model:tenure="selectedTenure"
      :available-tenures="availableTenures"
      :default-tenure="defaultTenure"
    />

    <!-- Price and Subnote Display -->
    <div aria-live="polite" class="flex flex-col justify-center">
      <div class="flex flex-col gap-1.5">
        <!-- Main Price Row with Animated Amount & Tenure -->
        <div class="flex items-baseline flex-wrap gap-x-2 gap-y-1">
          <div class="inline-flex items-baseline overflow-hidden">
            <span class="text-2xl sm:text-[32px] font-semibold text-on-primary-heading tracking-tight leading-none mr-0.5 select-none">
              ₹
            </span>
            <Transition name="num-slide" mode="out-in">
              <span
                :key="mode === 'full' ? `full-${formattedFullPrice}` : `mo-${formattedMonthlyPrice}`"
                class="text-2xl sm:text-[32px] font-semibold text-on-primary-heading tracking-tight leading-none inline-block font-numeric"
              >
                {{ mode === 'full' ? formattedFullPrice : formattedMonthlyPrice }}
              </span>
            </Transition>
          </div>

          <!-- /month × 12 months with horizontal expansion -->
          <Transition name="tenure-label-expand">
            <div
              v-if="mode === 'monthly'"
              class="tenure-label-container inline-flex items-baseline gap-1 text-sm sm:text-base font-normal text-on-primary-body overflow-hidden"
            >
              <span>/month &times;</span>
              <Transition name="num-slide" mode="out-in">
                <span :key="selectedTenure" class="font-medium text-on-primary-heading inline-block">
                  {{ selectedTenure }} months
                </span>
              </Transition>
            </div>
          </Transition>
        </div>

        <!-- Standard Disclaimer (Always Present) -->
        <div class="flex items-start gap-1.5 text-on-primary-body">
          <span class="text-xs sm:text-sm font-light italic">
            All prices are exclusive of framing, shipping, and govt taxes as applicable.
          </span>
          <UTooltip text="Full payment to purchase this artwork. Shipping and framing will be arranged after purchase.">
            <Icon name="material-symbols:info-outline" class="shrink-0 size-4 mt-0.5 opacity-70 hover:opacity-100 transition-opacity" />
          </UTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.font-numeric {
  font-variant-numeric: tabular-nums;
}

/* Snappy vertical flip/slide for amount and month numbers */
.num-slide-enter-active {
  transition: opacity 160ms ease-out, transform 180ms cubic-bezier(0.16, 1, 0.3, 1), filter 160ms ease-out;
}

.num-slide-leave-active {
  transition: opacity 120ms ease-in, transform 140ms cubic-bezier(0.7, 0, 0.84, 0), filter 120ms ease-in;
}

.num-slide-enter-from {
  opacity: 0;
  transform: translateY(8px);
  filter: blur(2px);
}

.num-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
  filter: blur(2px);
}

/* Tenure label (/month × 12 months) horizontal expand/collapse */
.tenure-label-container {
  max-width: 250px;
  white-space: nowrap;
}

.tenure-label-expand-enter-active {
  transition: max-width 280ms cubic-bezier(0.16, 1, 0.3, 1),
              opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
              transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
}

.tenure-label-expand-leave-active {
  transition: max-width 200ms cubic-bezier(0.7, 0, 0.84, 0),
              opacity 150ms ease,
              transform 200ms cubic-bezier(0.7, 0, 0.84, 0);
}

.tenure-label-expand-enter-from,
.tenure-label-expand-leave-to {
  max-width: 0 !important;
  opacity: 0;
  transform: translateX(-6px) scale(0.95);
}

.tenure-label-expand-enter-to,
.tenure-label-expand-leave-from {
  max-width: 250px;
  opacity: 1;
  transform: translateX(0) scale(1);
}
</style>

